import { useCallback, useEffect } from 'react';
import { normalizeEntries, buildTreeIndex, findFirstFile } from '../utils';
import { ApiEntry, TreeNode, TreeIndexMap } from '../types';
import { useUI, useEditorState } from '../contexts';

type UseFetchTreeParams = {
  token: string | null;
  apiBase: string;
  activeProject: string;
  cacheVersion: number;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
  setTree: (tree: TreeNode[]) => void;
  setTreeIndex: (index: TreeIndexMap) => void;
};

export function useFetchTree({
  token,
  apiBase,
  activeProject,
  cacheVersion,
  authFetch,
  setTree,
  setTreeIndex,
}: UseFetchTreeParams) {
  // Get UI state from context
  const { setIsFetchingTree, setStatus, setServerStateOverride, setGlobalError } = useUI();

  // Get editor state from context
  const { activeFile, setActiveFile, setOpenTabs } = useEditorState();
  const fetchTree = useCallback(
    async (projectId: string = activeProject) => {
      if (!token || !apiBase) return;
      setIsFetchingTree(true);
      setStatus('Proje dosyaları yükleniyor…');
      try {
        const res = await authFetch(`${apiBase}/flow/gitlike-get-directories-and-files`, {
          method: 'POST',
          body: JSON.stringify({ projectID: projectId }),
        });
        const data = await res.json();
        console.log('directories response', data);
        if (data?.status) {
          const entries: ApiEntry[] =
            data.result?.entries ||
            data.result?.data ||
            data.result?.files ||
            data.result?.directories ||
            data.result ||
            [];
          setServerStateOverride('ok');
          const normalized = normalizeEntries(entries);
          const map = buildTreeIndex(normalized);
          setTree(normalized);
          setTreeIndex(map);
          if (!activeFile) {
            const initial = findFirstFile(normalized);
            if (initial) {
              setActiveFile(initial.id);
              setOpenTabs((prev) => (prev.includes(initial.id) ? prev : [...prev, initial.id]));
            }
          }
          setStatus('Dosya listesi hazır');
        } else {
          setStatus('Liste alınamadı');
          setGlobalError('Liste alınamadı');
          setServerStateOverride('error');
        }
      } catch (error) {
        console.error('List error', error);
        setStatus('Liste hatası');
        setGlobalError('Liste hatası');
        setServerStateOverride('error');
      } finally {
        setIsFetchingTree(false);
      }
    },
    [activeFile, activeProject, authFetch, apiBase, token, setIsFetchingTree, setStatus, setServerStateOverride, setGlobalError, setTree, setTreeIndex, setActiveFile, setOpenTabs]
  );

  // Auto-fetch tree when token or cacheVersion changes
  useEffect(() => {
    if (!token || !apiBase) return;
    fetchTree(activeProject);
  }, [token, cacheVersion, activeProject, apiBase, fetchTree]);

  const handleReloadProject = useCallback(() => {
    fetchTree(activeProject);
  }, [activeProject, fetchTree]);

  return { fetchTree, handleReloadProject };
}
